import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRounded from "@mui/icons-material/HighlightOffRounded";
import { useEffect, useState } from "react";
import { black, green, greenLight, greyEnabled, purple, red, redLight } from "../../../../theme/colors";

export default function ReviewRedemptionDialog({
  open,
  onClose,
  onConfirm,
  action,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (comments: string) => void;
  action: "approve" | "reject" | null;
  loading?: boolean;
}) {
  const [comments, setComments] = useState("");

  useEffect(() => {
    if (open) setComments("");
  }, [open]);

  const isApprove = action === "approve";
  const iconColor = isApprove ? green : red;
  const iconBg = isApprove ? greenLight : redLight;
  const title = isApprove ? "Approve Redemption Request" : "Reject Redemption Request";

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ width: 420, maxWidth: "90vw", p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: iconBg,
                flexShrink: 0,
              }}
            >
              {isApprove ? (
                <CheckCircleOutlineRounded sx={{ color: iconColor, fontSize: 20 }} />
              ) : (
                <HighlightOffRounded sx={{ color: iconColor, fontSize: 20 }} />
              )}
            </Box>
            <Typography fontWeight={700} fontSize={17} color={black}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseRounded fontSize="small" sx={{ color: "#5B4A5A" }} />
          </IconButton>
        </Box>

        <Typography variant="body2" color={greyEnabled} mb={1.5}>
          {isApprove
            ? "This will move the request to In Progress and deduct the points from the user's balance."
            : "This will reject the request. No points will be deducted."}
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Remarks"
          placeholder="Enter remarks for this decision"
          value={comments}
          onChange={(e) => setComments(e.target.value.slice(0, 500))}
          helperText={`${comments.length}/500`}
        />

        <Box mt={2} display="flex" gap={1.5}>
          <Button
            fullWidth
            variant="outlined"
            disabled={loading}
            onClick={onClose}
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: "#F0D9F0" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading || !comments.trim()}
            onClick={() => onConfirm(comments.trim())}
            sx={{
              bgcolor: isApprove ? green : red,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: isApprove ? "#1F5C38" : "#A32020" },
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : isApprove ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
